import { useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { toast } from "sonner"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  ArrowUpRight01Icon,
  Copy01Icon,
  Loading01Icon,
  Mail01Icon,
  Tick01Icon,
} from "@hugeicons/core-free-icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { verify, sendInstallInstructions } from "@/lib/api/api"
import { useDetectSitePlatform } from "@/hooks/useDetectSitePlatform"
import {
  getPlatformGuide,
  platformGuides,
  PRIMARY_SELECTABLE_PLATFORMS,
  TOP_SELECTABLE_PLATFORMS,
  MORE_SELECTABLE_PLATFORMS,
} from "@/data/platform-guides"
import { PlatformInstallMedia } from "@/components/onboarding/PlatformInstallMedia"
import { PlatformGlyph } from "@/components/onboarding/PlatformGlyph"
import type { PlatformId } from "@/lib/types/platform"
import { parsePlatformFromApiPayload } from "@/lib/utils/detectPlatform"
import { track } from "@/lib/utils/track"
import { getTrackingSnippet } from "@/lib/utils/trackingSnippet"
import { generateScriptInstallPrompt, type ScriptInstallBuilder } from "@/lib/utils/generateAgentInstructions"
import { GtmInstructions } from "./GtmInstructions"
import { InstallSkipNotice } from "./InstallSkipNotice"

type InstallMethod = "manual" | "gtm"

/** The agent path's install routes, in the order they're offered. */
const AGENT_BUILDERS: { id: ScriptInstallBuilder; label: string }[] = [
  { id: "webflow", label: "Webflow" },
  { id: "framer", label: "Framer" },
  { id: "github", label: "Code or GitHub" },
]

/** Live verification: one call immediately, then every 3s for a 60s window. */
const POLL_INTERVAL_MS = 3000
const POLL_WINDOW_MS = 60000

type InstallScriptPanelProps = {
  domain: string
  /** Loader id for `hmbl.min.js?id=` — copy, verify, and developer email. Never a propertyId. */
  scriptId: string
  token: string
  propertyId?: string
  allowContinueUnverified?: boolean
  /** Tests already drafted for this site. Named in the skip line when present. */
  draftedTestCount?: number
  onCopied?: (platform: PlatformId, method: InstallMethod) => void
  onIdCopied?: () => void
  onMethodChange?: (method: InstallMethod) => void
  onInstructionsExpanded?: (platform: PlatformId) => void
  onVerified?: (attempts: number) => void
  /**
   * The moment tracking went live, as an ISO string. `validate-setup-v2`
   * returns no timestamp of its own (it answers `scriptInstalledHead` and the
   * platform signals only), so this is the client clock at the tick that saw
   * the install, and it is read as "first pageview <n>s ago" on the site card.
   * If the endpoint ever carries a real first-hit time, prefer it here.
   */
  onFirstHit?: (isoTime: string) => void
  onVerificationFailed?: (attempt: number) => void
  onContinueUnverified?: (failCount: number) => void
  onPlatformDetected?: (platform: PlatformId, source: string) => void
  /**
   * Agent path (onboarding step 02 = "With your agent"): the agent is already
   * connected, so the step leads with a prompt for it and the paste box sits
   * behind "Show the code". Paper 08, A4.
   */
  agentFirst?: boolean
}

/** The #1 verify-failure cause per platform, shown in the "not detected" panel. */
const PLATFORM_FAIL_TIPS: Partial<Record<PlatformId, string>> = {
  webflow:
    "Publish to the domain you added here. Publishing only to the .webflow.io staging domain won't verify your custom domain.",
  framer:
    "Paste under Site settings → General → Custom Code → Start of <head>, then hit Publish. Saving alone isn't enough.",
  wordpress:
    "Using a caching plugin (WPCode, LiteSpeed, WP Rocket)? Clear the cache after saving or the old page keeps serving.",
  wix: "Make sure placement is set to Head and the code is applied to All Pages.",
  squarespace:
    "Code Injection requires a Squarespace Business plan or higher. Check Settings → Developer Tools.",
  shopify: "Save theme.liquid in your live theme. Edits to a draft theme won't show on your store.",
  nextjs: "Deploy your changes first. We can't verify a local dev server.",
  bubble: "Deploy to live. Testing on /version-test won't verify.",
  ghost: "Confirm you clicked Save in Settings → Code Injection → Site Header.",
  kajabi: "Paste in Header Code (not Footer) under Settings → Site Details → Page Scripts.",
  podia: "Paste in Header code under Settings → Advanced → Custom code, then Save changes.",
  lovable: "Publish/deploy first. The editor preview URL usually won't verify.",
  beehiiv: "Save Website custom code, then open your public Beehiiv site URL (not the admin).",
  kit: "Publish the landing page / Creator Profile after pasting Head / custom HTML.",
  bolt: "Verify on the deployed Netlify/Vercel URL. Bolt preview won't count.",
  astro: "Use is:inline on the script and verify the production build, not npm run dev.",
  vuejs: "Deploy production first. Local Vite/Webpack servers won't verify.",
  replit: "Use a published .replit.app URL, not a private workspace preview.",
  django: "Confirm the script is in your base template <head> on the production host.",
  laravel: "Confirm the script is in your Blade layout <head> on the production host.",
  reactrouter: "Verify on the production build. SPA shells must load the script once in index.html.",
  v0: "Deploy to production (Vercel). v0 preview URLs often won't verify.",
}

/**
 * The time to report as the first pageview. `verify` (POST validate-setup-v2)
 * returns `{ scriptInstalled, scriptInstalledHead, ...platform signals }` and no
 * timestamp, so a server-supplied field is used when one appears and the client
 * clock is the fallback. Checked against the live payload on 2026-09-10.
 */
function firstHitTimestamp(response: unknown): string {
  const candidate = (response as Record<string, unknown> | null)?.firstHitAt
  if (typeof candidate === "string" && !Number.isNaN(Date.parse(candidate))) {
    return new Date(candidate).toISOString()
  }
  return new Date().toISOString()
}

export function InstallScriptPanel({
  domain,
  scriptId,
  token,
  propertyId,
  allowContinueUnverified = false,
  draftedTestCount,
  onCopied,
  onIdCopied,
  onMethodChange,
  onInstructionsExpanded,
  onVerified,
  onFirstHit,
  onVerificationFailed,
  onContinueUnverified,
  onPlatformDetected,
  agentFirst = false,
}: InstallScriptPanelProps) {
  const [installMethod, setInstallMethod] = useState<InstallMethod>("manual")
  const [verificationState, setVerificationState] = useState<
    "idle" | "checking" | "success" | "failed"
  >("idle")
  const [verificationFailCount, setVerificationFailCount] = useState(0)
  const [codeCopied, setCodeCopied] = useState(false)
  const [developerEmail, setDeveloperEmail] = useState("")
  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [showAllPlatforms, setShowAllPlatforms] = useState(false)
  const [showMorePlatforms, setShowMorePlatforms] = useState(false)
  const [stepsExpanded, setStepsExpanded] = useState(false)
  const [showDevHandoff, setShowDevHandoff] = useState(false)
  const [pasteHelpOpen, setPasteHelpOpen] = useState(false)

  const {
    platform,
    detection,
    isDetecting,
    isManual,
    setPlatform,
    applyDetection,
  } = useDetectSitePlatform({ domain, token, propertyId })

  const guide = useMemo(() => getPlatformGuide(platform), [platform])
  const platformLabel = guide.name === "Other / Manual" ? "your site" : guide.name

  // One row by default (the six most common builders), the full list behind
  // "More platforms". Fifteen chips wrapped to three rows and were the single
  // biggest reason this step could not fit a laptop viewport. A platform
  // detected outside the top row is appended so the active chip is always
  // visible without expanding.
  const pickerPlatformIds = useMemo(() => {
    if (showMorePlatforms) {
      return [...PRIMARY_SELECTABLE_PLATFORMS, ...MORE_SELECTABLE_PLATFORMS]
    }
    if (platform !== "generic" && !TOP_SELECTABLE_PLATFORMS.includes(platform)) {
      return [...TOP_SELECTABLE_PLATFORMS, platform]
    }
    return TOP_SELECTABLE_PLATFORMS
  }, [showMorePlatforms, platform])

  const trackedDetectionRef = useRef<string | null>(null)
  const pasteHelpTouchedRef = useRef(false)

  useEffect(() => {
    if (!detection || isManual) return
    const key = `${domain}:${detection.platform}:${detection.source}`
    if (trackedDetectionRef.current === key) return
    trackedDetectionRef.current = key
    onPlatformDetected?.(detection.platform, detection.source)
    onInstructionsExpanded?.(detection.platform)
  }, [domain, detection, isManual, onPlatformDetected, onInstructionsExpanded])

  // The paste help opens itself only when we actually know the platform, so the
  // steps we auto-expand are the right ones. Detection resolves async, hence the
  // effect rather than a lazy initial state.
  useEffect(() => {
    if (pasteHelpTouchedRef.current) return
    if (isManual || !detection) return
    if (detection.platform === "generic") return
    setPasteHelpOpen(true)
  }, [detection, isManual])

  const embedCode = getTrackingSnippet(scriptId)

  /* ---------------------------------------------------------------------- */
  /* Live verification poll                                                  */
  /* ---------------------------------------------------------------------- */

  const pollTimerRef = useRef<number | null>(null)
  const pollDeadlineRef = useRef(0)
  const isPollingRef = useRef(false)
  const attemptsRef = useRef(0)
  const failCountRef = useRef(0)
  const hiddenAtRef = useRef<number | null>(null)
  const tickRef = useRef<() => void>(() => {})

  const clearPollTimer = () => {
    if (pollTimerRef.current !== null) {
      window.clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }

  const stopPolling = () => {
    isPollingRef.current = false
    hiddenAtRef.current = null
    clearPollTimer()
  }

  const registerFailure = () => {
    failCountRef.current += 1
    setVerificationFailCount(failCountRef.current)
    onVerificationFailed?.(failCountRef.current)
  }

  const runPollTick = async () => {
    pollTimerRef.current = null
    if (!isPollingRef.current || !scriptId || !token) return
    // Paused while the tab is in the background. The visibility handler resumes.
    if (typeof document !== "undefined" && document.hidden) return

    attemptsRef.current += 1
    let installed = false
    let response: unknown = null
    try {
      response = await verify(domain, scriptId, token)

      const fromVerify = parsePlatformFromApiPayload(response)
      if (fromVerify && fromVerify.confidence >= 0.5) {
        applyDetection({
          platform: fromVerify.platform,
          confidence: fromVerify.confidence,
          source: "verify",
          signals: fromVerify.signals,
          label: fromVerify.label,
        })
      }

      installed = Boolean((response as { scriptInstalledHead?: boolean } | null)?.scriptInstalledHead)
    } catch {
      // A single failed request is not a verdict. The 60s window is.
    }

    if (!isPollingRef.current) return

    if (installed) {
      stopPolling()
      setVerificationState("success")
      onFirstHit?.(firstHitTimestamp(response))
      onVerified?.(attemptsRef.current)
      return
    }

    if (Date.now() + POLL_INTERVAL_MS > pollDeadlineRef.current) {
      stopPolling()
      setVerificationState("failed")
      registerFailure()
      return
    }

    if (typeof document !== "undefined" && document.hidden) return
    pollTimerRef.current = window.setTimeout(() => {
      void tickRef.current()
    }, POLL_INTERVAL_MS)
  }

  useEffect(() => {
    tickRef.current = () => {
      void runPollTick()
    }
  })

  useEffect(() => {
    const handleVisibility = () => {
      if (!isPollingRef.current) return
      if (document.hidden) {
        hiddenAtRef.current = Date.now()
        clearPollTimer()
        return
      }
      // Time spent on another tab is usually time spent publishing the site, so
      // it is given back rather than counted against the window.
      if (hiddenAtRef.current !== null) {
        pollDeadlineRef.current += Date.now() - hiddenAtRef.current
        hiddenAtRef.current = null
      }
      if (pollTimerRef.current === null) {
        pollTimerRef.current = window.setTimeout(() => {
          void tickRef.current()
        }, 0)
      }
    }
    document.addEventListener("visibilitychange", handleVisibility)
    return () => document.removeEventListener("visibilitychange", handleVisibility)
  }, [])

  useEffect(
    () => () => {
      isPollingRef.current = false
      if (pollTimerRef.current !== null) {
        window.clearTimeout(pollTimerRef.current)
        pollTimerRef.current = null
      }
    },
    [],
  )

  const handleVerify = () => {
    if (!scriptId || !token) return
    if (isPollingRef.current) return
    attemptsRef.current = 0
    isPollingRef.current = true
    hiddenAtRef.current = null
    pollDeadlineRef.current = Date.now() + POLL_WINDOW_MS
    setVerificationState("checking")
    void tickRef.current()
  }

  const handleMethodChange = (method: InstallMethod) => {
    if (method === installMethod) return
    stopPolling()
    setInstallMethod(method)
    setVerificationState("idle")
    setVerificationFailCount(0)
    failCountRef.current = 0
    onMethodChange?.(method)
  }

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(embedCode)
      setCodeCopied(true)
      toast.success("Code copied to clipboard")
      onCopied?.(platform, installMethod)
      setTimeout(() => setCodeCopied(false), 2500)
    } catch {
      toast.error("Failed to copy code")
    }
  }

  // Webflow and Framer ship MCP servers that can write head code, so an agent
  // with one connected can do the install. The snippet is public; no key here.
  const agentBuilder: ScriptInstallBuilder | null =
    platform === "webflow" || platform === "framer" ? platform : null
  const [agentPromptCopied, setAgentPromptCopied] = useState(false)
  const handleCopyAgentPrompt = async () => {
    if (!agentBuilder) return
    try {
      await navigator.clipboard.writeText(
        generateScriptInstallPrompt(agentBuilder, { snippet: embedCode, domain }),
      )
      setAgentPromptCopied(true)
      track("install_agent_prompt_copied", token, { platform: agentBuilder })
      setTimeout(() => setAgentPromptCopied(false), 2500)
    } catch {
      toast.error("Failed to copy the prompt")
    }
  }

  // Agent path: Webflow and Framer go through the builder's MCP, anything else
  // through the repo (the agent opens a pull request).
  const [agentPickOverride, setAgentPickOverride] = useState<ScriptInstallBuilder | null>(null)
  const agentPick: ScriptInstallBuilder = agentPickOverride ?? agentBuilder ?? "github"
  const [showCode, setShowCode] = useState(false)
  const agentFirstPrompt = useMemo(
    () => generateScriptInstallPrompt(agentPick, { snippet: embedCode, domain }),
    [agentPick, embedCode, domain],
  )
  const handleCopyAgentFirstPrompt = async () => {
    try {
      await navigator.clipboard.writeText(agentFirstPrompt)
      setAgentPromptCopied(true)
      track("install_agent_prompt_copied", token, { platform: agentPick, agent_first: true })
      setTimeout(() => setAgentPromptCopied(false), 2500)
      // Start watching for the first visit as soon as the agent has the prompt.
      handleVerify()
    } catch {
      toast.error("Failed to copy the prompt")
    }
  }

  const handleEmailDeveloper = async () => {
    if (!developerEmail.trim() || !scriptId || !token) return
    setIsSendingEmail(true)
    const steps = guide.shortSteps.join(" → ")
    try {
      await sendInstallInstructions(developerEmail, domain, scriptId, platform, steps, token)
      setEmailSent(true)
      toast.success(`Instructions sent to ${developerEmail}`)
    } catch {
      toast.error("Failed to send email. Please try again.")
    } finally {
      setIsSendingEmail(false)
    }
  }

  const handleSelectPlatform = (next: PlatformId) => {
    setPlatform(next)
    setShowAllPlatforms(false)
    setStepsExpanded(false)
    onInstructionsExpanded?.(next)
    onPlatformDetected?.(next, "manual")
    if (token) {
      track("onboarding_platform_selected", token, {
        platform: next,
        previous: detection?.platform,
        source: "manual",
      })
    }
  }

  const handleTogglePasteHelp = () => {
    pasteHelpTouchedRef.current = true
    setPasteHelpOpen((open) => {
      if (!open) onInstructionsExpanded?.(platform)
      return !open
    })
  }

  const detectedBadge = (() => {
    if (isManual) return "Selected"
    if (isDetecting) return "Detecting…"
    if (detection?.source === "firecrawl" && platform !== "generic") return "Detected"
    if (detection?.source === "html" && platform !== "generic") return "Detected"
    if (detection?.source === "domain" && platform !== "generic") return "Detected"
    if (detection?.source === "verify" && platform !== "generic") return "Detected"
    return null
  })()

  // Custom/undetected sites are the users who most need guidance — for them the
  // platform picker is open by default instead of hidden behind "Wrong platform?".
  const pickerForcedOpen = platform === "generic" && !isDetecting
  const pickerOpen = showAllPlatforms || pickerForcedOpen

  // GTM installs are verified via traffic, not head crawl — lower the bar to one failed attempt
  const gtmContinueThreshold = 1
  const showContinueUnverified =
    allowContinueUnverified &&
    (installMethod === "gtm"
      ? verificationFailCount >= gtmContinueThreshold
      : verificationFailCount >= 2)

  const domainLabel = domain || "your site"
  const platformTip = PLATFORM_FAIL_TIPS[platform]

  return (
    <div className="space-y-3 min-w-0">
      {agentFirst && !showCode ? (
        <div className="rounded-lg border border-border/30 overflow-hidden min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/30 px-3 py-2.5">
            <div role="radiogroup" aria-label="How your agent adds it" className="flex flex-wrap items-center gap-1">
              {AGENT_BUILDERS.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  role="radio"
                  aria-checked={agentPick === b.id}
                  onClick={() => setAgentPickOverride(b.id)}
                  className={cn(
                    "rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors",
                    agentPick === b.id
                      ? "bg-muted text-foreground shadow-[0_0_0_1px_var(--input)]"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {b.label}
                </button>
              ))}
            </div>
            {agentBuilder && agentPick === agentBuilder ? (
              <span className="text-[11px] text-muted-foreground">Detected on {domainLabel}</span>
            ) : null}
          </div>
          <div className="space-y-2 px-3 py-3">
            <div>
              <p className="text-xs font-medium text-foreground">Send this to your agent</p>
              <p className="text-[11px] leading-snug text-muted-foreground">
                {agentPick === "github"
                  ? "It edits your site's code in Cursor, Claude Code or Codex, or through GitHub, and opens a pull request. Merge it and we see the first visit."
                  : `It uses the official ${agentPick === "webflow" ? "Webflow" : "Framer"} MCP. If it isn't connected, your agent tells you how to add it.`}
              </p>
            </div>
            <div className="flex items-start gap-2 rounded-md border border-border/30 bg-muted/30 p-2.5">
              <p className="line-clamp-3 min-w-0 flex-1 whitespace-pre-line text-[11px] leading-[17px] text-foreground/80">
                {agentFirstPrompt}
              </p>
              <Button
                onClick={handleCopyAgentFirstPrompt}
                variant="outline"
                size="sm"
                className="h-7 shrink-0 gap-1.5 px-2.5 text-[11px]"
              >
                <HugeiconsIcon icon={agentPromptCopied ? Tick01Icon : Copy01Icon} size={12} />
                {agentPromptCopied ? "Copied" : "Copy prompt"}
              </Button>
            </div>
          </div>
          <div className="border-t border-border/30 px-3 py-2">
            <button
              type="button"
              onClick={() => setShowCode(true)}
              className="inline-flex min-h-6 items-center text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            >
              Rather do it yourself? Show the code
            </button>
          </div>
        </div>
      ) : (
      <>
      {/* Install method selector */}
      <div className="flex rounded-md border border-border/40 overflow-hidden">
        {(["manual", "gtm"] as InstallMethod[]).map((method) => (
          <button
            key={method}
            type="button"
            onClick={() => handleMethodChange(method)}
            className={cn(
              // Active = muted fill, never orange. Orange is brand + primary CTA
              // only (DESIGN.md); a full-width orange tab competes with the real
              // CTA further down the page.
              "flex-1 py-1.5 text-[11px] font-medium transition-colors",
              installMethod === method
                ? "bg-muted text-foreground"
                : "bg-transparent text-muted-foreground hover:bg-muted/40",
              method === "manual" && "border-r border-border/40"
            )}
          >
            {method === "manual" ? "Manual (paste code)" : "Google Tag Manager"}
          </button>
        ))}
      </div>

      {installMethod === "gtm" ? (
        <GtmInstructions
          scriptId={scriptId}
          onIdCopied={() => onIdCopied?.()}
        />
      ) : (
        /* The artifact they came for, first. Copy is a secondary control on the
           header; the check below it is the only filled button on the step. */
        <div className="rounded-lg border border-border/30 bg-muted/10 overflow-hidden min-w-0">
          <div className="flex items-center justify-between gap-2 px-3 pt-2.5">
            <div className="min-w-0">
              <p className="text-xs font-medium text-foreground truncate">Your tracking code</p>
              <p className="text-[11px] text-muted-foreground truncate">
                Paste it in the &lt;head&gt; of {domainLabel}, then publish.
              </p>
            </div>
            <Button
              onClick={handleCopyCode}
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 px-2.5 text-[11px] shrink-0"
            >
              {codeCopied ? (
                <>
                  <HugeiconsIcon icon={Tick01Icon} size={12} className="text-success dark:text-success" /> Copied
                </>
              ) : (
                <>
                  <HugeiconsIcon icon={Copy01Icon} size={12} /> Copy code
                </>
              )}
            </Button>
          </div>
          {/* Preserve line breaks; scroll horizontally instead of mid-word wrap (break-all). */}
          <pre
            role="group"
            tabIndex={0}
            aria-label="Tracking code"
            className="mx-3 mb-2.5 mt-1.5 max-h-[min(132px,20vh)] overflow-auto rounded-md border border-border/30 bg-muted/30 px-2.5 py-2 text-[11px] font-mono leading-relaxed text-foreground/90 whitespace-pre [overflow-wrap:normal] [word-break:normal] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
            <code className="block min-w-max">{embedCode}</code>
          </pre>
          {agentBuilder ? (
            <div className="flex items-center justify-between gap-3 border-t border-border/30 px-3 py-2.5">
              <p className="min-w-0 text-[11px] leading-snug text-muted-foreground">
                <span className="font-medium text-foreground">{guide.name} connected to your agent?</span>{" "}
                Copy a prompt and your agent adds the script for you.
              </p>
              <Button
                onClick={handleCopyAgentPrompt}
                variant="outline"
                size="sm"
                className="h-7 shrink-0 gap-1.5 px-2.5 text-[11px]"
              >
                <HugeiconsIcon icon={agentPromptCopied ? Tick01Icon : Copy01Icon} size={12} />
                {agentPromptCopied ? "Copied" : "Copy prompt"}
              </Button>
            </div>
          ) : null}
        </div>
      )}
      </>
      )}

      {/* Verification strip: waiting → live → not detected. Sits directly above
          the button so the outcome lands where the action was taken. */}
      <AnimatePresence initial={false}>
        {verificationState !== "idle" && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{ overflow: "hidden" }}
          >
            {verificationState === "checking" && (
              <div
                className="flex items-center gap-2.5 rounded-md border border-border/40 bg-muted/20 px-3 py-2.5"
                role="status"
                aria-live="polite"
              >
                <span className="relative flex size-2 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-muted-foreground/60 motion-reduce:animate-none" />
                  <span className="relative inline-flex size-2 rounded-full bg-muted-foreground" />
                </span>
                <p className="text-[11px] text-muted-foreground leading-snug min-w-0">
                  Waiting for the first pageview from <strong className="font-medium text-foreground">{domainLabel}</strong>
                </p>
              </div>
            )}

            {verificationState === "success" && (
              <div
                className="flex items-center gap-2.5 rounded-md border border-success/30 bg-success/10 px-3 py-2.5"
                role="status"
                aria-live="polite"
              >
                <HugeiconsIcon
                  icon={Tick01Icon}
                  size={14}
                  className="shrink-0 text-success dark:text-success"
                />
                <p className="text-xs font-medium text-success dark:text-success">
                  Tracking is live
                </p>
              </div>
            )}

            {verificationState === "failed" && (
              <div
                className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2.5 space-y-1.5"
                role="status"
                aria-live="polite"
              >
                <p className="text-xs font-medium text-warning dark:text-warning">
                  Not detected yet
                </p>
                {installMethod === "gtm" ? (
                  <p className="text-[11px] text-warning dark:text-warning leading-snug">
                    GTM loads the script after publish, which can take a few minutes. Check again, or continue and we'll confirm once we see traffic.
                  </p>
                ) : (
                  <>
                    {platformTip && (
                      <p className="text-[11px] font-medium text-warning dark:text-warning leading-snug">
                        {guide.name} tip: {platformTip}
                      </p>
                    )}
                    <ul className="text-[11px] text-warning dark:text-warning space-y-0.5 ml-3 list-disc">
                      <li>Make sure the script is in the <code className="font-mono">&lt;head&gt;</code> section</li>
                      <li>Publish or deploy after pasting. Drafts won't verify</li>
                      <li>Clear your site's cache, wait a minute, then check again</li>
                    </ul>
                  </>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* The one action that moves this step forward. */}
      {verificationState !== "success" && (
        <Button
          onClick={handleVerify}
          disabled={verificationState === "checking"}
          variant="default"
          className={cn(
            "w-full gap-1.5 h-10",
            // The app-wide --primary measures 4.06:1 against a white label. The
            // wizard's filled action uses a darker local token so the label
            // clears 4.5:1. Changing --primary itself is an app-wide DESIGN.md
            // decision, flagged in the spec rather than made here.
            "bg-[var(--onboarding-action,oklch(0.54_0.21_42))] text-white",
            "hover:bg-[var(--onboarding-action-hover,oklch(0.5_0.21_42))]",
          )}
        >
          {verificationState === "checking" ? (
            <>
              <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin motion-reduce:animate-none" />
              Checking your site...
            </>
          ) : verificationState === "failed" ? (
            "Check again"
          ) : (
            <>
              <HugeiconsIcon icon={Tick01Icon} size={14} />{" "}
              {agentFirst && !showCode ? "Check for the first visit" : "I've added it, check now"}
            </>
          )}
        </Button>
      )}

      {showContinueUnverified && verificationState !== "success" && (
        <InstallSkipNotice
          draftedTestCount={draftedTestCount}
          onSkip={() => onContinueUnverified?.(verificationFailCount)}
        />
      )}

      {/* Agent path: the paste help and developer handoff wait behind "Show the code". */}
      {agentFirst && !showCode ? null : (
      <>
      {/* Developer handoff — a text link, not a competing control. */}
      <div className="min-w-0">
        {emailSent ? (
          <div className="flex items-start gap-2">
            <HugeiconsIcon icon={Tick01Icon} size={12} className="text-success dark:text-success shrink-0 mt-0.5" />
            <p className="text-[11px] text-success dark:text-success/80">
              Instructions sent to <strong>{developerEmail}</strong>.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            <button
              type="button"
              onClick={() => setShowDevHandoff((v) => !v)}
              className="inline-flex min-h-6 items-center text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            >
              Not doing it yourself? Send this to your developer
            </button>
            {showDevHandoff && (
              <div className="flex gap-2 min-w-0">
                <Input
                  type="email"
                  placeholder="dev@yourteam.com"
                  value={developerEmail}
                  onChange={(event) => setDeveloperEmail(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault()
                      handleEmailDeveloper()
                    }
                  }}
                  className="h-8 flex-1 min-w-0 text-xs"
                />
                <Button
                  onClick={handleEmailDeveloper}
                  disabled={!developerEmail.trim() || isSendingEmail}
                  variant="outline"
                  size="sm"
                  className="gap-1.5 h-8 shrink-0"
                >
                  {isSendingEmail ? (
                    <>
                      <HugeiconsIcon icon={Loading01Icon} size={12} className="animate-spin motion-reduce:animate-none" /> Sending...
                    </>
                  ) : (
                    <>
                      <HugeiconsIcon icon={Mail01Icon} size={12} /> Send
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {installMethod === "manual" && (
        /* Reference material, below the action. Open by default only when we
           detected the platform, so the steps we expand are the right ones. */
        <div className="rounded-lg border border-border/30 overflow-hidden min-w-0">
          <button
            type="button"
            onClick={handleTogglePasteHelp}
            aria-expanded={pasteHelpOpen}
            className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left bg-muted/15 hover:bg-muted/25 transition-colors"
          >
            <span className="flex items-center gap-2 min-w-0 flex-wrap">
              {platform !== "generic" && <PlatformGlyph platform={platform} />}
              <span className="text-xs font-semibold text-foreground">
                {isDetecting && !isManual ? (
                  <span className="inline-flex items-center gap-1.5">
                    <HugeiconsIcon icon={Loading01Icon} size={12} className="animate-spin motion-reduce:animate-none text-muted-foreground" />
                    Detecting your site type…
                  </span>
                ) : platform !== "generic" ? (
                  <>Where do I paste this on {guide.name}?</>
                ) : (
                  <>Where do I paste this?</>
                )}
              </span>
              {detectedBadge && (
                <span
                  className={cn(
                    "text-[11px] font-medium rounded-full border px-1.5 py-0.5",
                    isDetecting && !isManual
                      ? "border-border/30 text-muted-foreground bg-muted/40"
                      : "border-success/30 text-success dark:text-success bg-success/10",
                  )}
                >
                  {detectedBadge}
                </span>
              )}
            </span>
            <span className="text-[11px] text-muted-foreground shrink-0">
              {pasteHelpOpen ? "Hide" : "Show"}
            </span>
          </button>

          <AnimatePresence initial={false}>
            {pasteHelpOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.18 }}
                style={{ overflow: "hidden" }}
              >
                <div className="border-t border-border/30">
                  {/* Platform picker */}
                  <div className="px-3 py-2.5 border-b border-border/30">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[11px] text-muted-foreground leading-snug min-w-0">
                        {platform !== "generic"
                          ? `Takes ${guide.setupTime ?? "~2 min"}. Paste, publish, then check.`
                          : "Pick your platform for exact steps, or follow the manual guide below."}
                      </p>
                      {!pickerForcedOpen && (
                        <button
                          type="button"
                          onClick={() => setShowAllPlatforms((v) => !v)}
                          className="text-[11px] text-muted-foreground hover:text-foreground shrink-0 underline-offset-2 hover:underline"
                        >
                          {showAllPlatforms ? "Hide" : "Wrong platform?"}
                        </button>
                      )}
                    </div>

                    <AnimatePresence initial={false}>
                      {pickerOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.18 }}
                          style={{ overflow: "hidden" }}
                        >
                          <div className="pt-2 flex flex-wrap gap-1.5">
                            {pickerPlatformIds.map((id) => {
                              const item = platformGuides.find((g) => g.id === id)
                              if (!item) return null
                              const active = platform === id
                              return (
                                <button
                                  key={id}
                                  type="button"
                                  onClick={() => handleSelectPlatform(id)}
                                  className={cn(
                                    "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors",
                                    active
                                      ? "border-border bg-muted text-foreground"
                                      : "border-border/50 bg-card text-muted-foreground hover:border-border/80 hover:text-foreground",
                                  )}
                                >
                                  <PlatformGlyph platform={id} size={14} className="h-3.5 w-3.5" />
                                  {item.name}
                                </button>
                              )
                            })}
                            {!showMorePlatforms && (
                              <button
                                type="button"
                                onClick={() => setShowMorePlatforms(true)}
                                className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-border/50 bg-card px-2 py-1 text-[11px] font-medium text-muted-foreground hover:border-border/80 hover:text-foreground transition-colors"
                              >
                                More platforms
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleSelectPlatform("generic")}
                              className={cn(
                                "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors",
                                platform === "generic"
                                  ? "border-border bg-muted text-foreground"
                                  : "border-border/50 bg-card text-muted-foreground hover:border-border/80 hover:text-foreground",
                              )}
                            >
                              Other
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Show it, then say it. Preview left, written steps right. */}
                  <div className="px-3 py-2.5">
                    <div className="grid gap-3 lg:grid-cols-2 lg:gap-4">
                      <div className="min-w-0">
                        <PlatformInstallMedia key={guide.id} guide={guide} videoCollapsedByDefault />
                      </div>

                      <div className="min-w-0 space-y-1.5">
                        <button
                          type="button"
                          onClick={() => setStepsExpanded((v) => !v)}
                          className="flex w-full items-center justify-between gap-2 text-left"
                        >
                          <p className="text-[11px] font-medium text-foreground">
                            {guide.name === "Other / Manual" ? "Manual steps" : `${guide.name} steps`}
                          </p>
                          <span className="text-[11px] text-muted-foreground tabular-nums">
                            {stepsExpanded ? "Hide" : `Show ${guide.steps.length}`}
                          </span>
                        </button>

                        {!stepsExpanded && (
                          <ol className="space-y-1">
                            {guide.shortSteps.map((step, index) => (
                              <li key={`${guide.id}-short-${index}`} className="flex items-start gap-2">
                                <span className="shrink-0 text-[11px] font-semibold text-muted-foreground tabular-nums mt-px">
                                  {index + 1}.
                                </span>
                                <p className="text-[11px] text-muted-foreground leading-snug min-w-0">{step}</p>
                              </li>
                            ))}
                          </ol>
                        )}

                        <AnimatePresence initial={false}>
                          {stepsExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.16 }}
                              style={{ overflow: "hidden" }}
                            >
                              <ol className="space-y-2 pt-0.5">
                                {guide.steps.map((step, index) => (
                                  <li key={`${guide.id}-${step.title}`} className="flex items-start gap-2.5">
                                    <div className="shrink-0 w-4 h-4 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-[11px] font-semibold mt-px">
                                      {index + 1}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-[11px] font-medium text-foreground leading-snug">{step.title}</p>
                                      <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                                        {step.description}
                                      </p>
                                    </div>
                                  </li>
                                ))}
                              </ol>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <a
                          href={guide.docsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-foreground/80 hover:text-foreground"
                        >
                          Full {platformLabel === "your site" ? "" : `${guide.name} `}guide
                          <HugeiconsIcon icon={ArrowUpRight01Icon} size={12} />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
      </>
      )}
    </div>
  )
}
